import * as entityBackend from '@/backend/entity'
import { useWatchProject } from '@/data/Agents/useWatchProject'
import { useEntityStore } from '@/data/useEntityStore'
import type { Entity } from '@/modules/Project/types'
import { nonNullable } from '@/shared/utils/typeAssertions'
import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue'

/**
 * Fetches all entities for a given project and workspace.
 */
export function useAllEntitiesQuery(props: {
  projectId: MaybeRefOrGetter<string | undefined | null>
  workspaceId: Ref<string>
}) {
  const entityStore = useEntityStore()

  useWatchProject({
    workspaceId: computed(() => toValue(props.workspaceId)),
    projectId: computed(() => toValue(props.projectId)),
  })

  const query = useQuery({
    queryKey: [props.projectId, 'entities'],
    staleTime: 2_000,
    enabled: () => !!toValue(props.projectId) && !!toValue(props.workspaceId),
    queryFn: async () => {
      const agentId = nonNullable(toValue(props.projectId))
      const workspaceId = nonNullable(toValue(props.workspaceId))

      const allData: Entity[] = []

      let lastCursor: string | null = null
      let hasNextPage = true

      while (hasNextPage) {
        const result = await entityBackend.listEntities({
          limit: 1000,
          after: lastCursor,
          projectId: agentId,
          workspaceId,
        })

        if (!result.ok) throw new Error(result.error.message)

        hasNextPage = result.data.metadata.has_next_page
        lastCursor = result.data.metadata.end_cursor

        allData.push(
          ...result.data.data.map((item) => entityStore.pushEntityJSON(item, workspaceId)),
        )
      }

      return allData
    },
  })

  const entities = computed(() => {
    if (!query.data.value) return []
    const agentId = toValue(props.projectId)
    if (!agentId) return []
    return entityStore.findEntitiesForAgent(agentId)
  })

  return { ...query, entities }
}
